'use client';

import Image from 'next/image';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function AltGozKapagiAmeliyatindaIzKalmasi() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/alt-goz-kapagi-ameliyatinda-iz-kalmasi-gercek-mi-korku-mu';
  const pageTitle = 'Alt Göz Kapağı Ameliyatında İz Kalması: Gerçek mi, Korku mu?';
  const pageDescription = 'Alt göz kapağı estetiği (blefaroplasti) sonrasında iz kalır mı? Kesinin yeri, cilt tipi, dikiş tekniği ve iyileşme süreci hakkında tüm bilimsel detaylar.';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Alt Göz Kapağı Ameliyatında İz Kalması', href: '/tr/blog/alt-goz-kapagi-ameliyatinda-iz-kalmasi-gercek-mi-korku-mu' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Alt göz kapağı ameliyatı iz kalması ve iyileşme süreci"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-emerald-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Estetik Cerrahi
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Alt Göz Kapağı Ameliyatında İz Kalması: Gerçek mi, Korku mu?
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>16 Mart 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>19 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-emerald-600 text-white rounded-full hover:bg-emerald-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-emerald-400 text-white rounded-full hover:bg-emerald-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-emerald-700 text-white rounded-full hover:bg-emerald-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Alt göz kapağı ameliyatı, göz çevresindeki torbaları ve fazla deriyi alarak daha dinç bir görünüm sağlar.
                Ancak birçok hasta bu ameliyatı yaptırmadan önce aynı soruyu sorar:
              </p>

              <div className="bg-emerald-50 border-l-4 border-emerald-400 p-6 mb-6">
                <p className="text-emerald-800 font-medium text-lg">
                  "Alt göz kapağı ameliyatında iz kalır mı?"
                </p>
                <p className="text-emerald-800 mt-2">
                  Bu yazı, o sorunun tüm cevabını bilimsel verilerle, cerrahi deneyimle ve hasta gözünden sade biçimde açıklıyor.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1️⃣ Ameliyatın Amacı
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Zamanla göz altındaki yağ dokuları öne çıkar ve cilt elastikiyetini kaybeder.
                Alt blefaroplasti ameliyatı bu fazla yağ dokusunu düzenler, cildi gerginleştirir ve torbaları ortadan kaldırır.
              </p>

              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <h4 className="font-semibold text-blue-900 mb-4">Uygulama:</h4>
                <ul className="list-disc list-inside space-y-2 text-blue-800">
                  <li>Genel veya lokal anestezi altında yapılır</li>
                  <li>Ortalama 45–60 dakika sürer</li>
                  <li>Göz altı torbası, sarkma ve kırışıklık aynı anda tedavi edilir</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2️⃣ İz Nerede Oluşur?
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Alt göz kapağı ameliyatında iki temel teknik vardır:
              </p>

              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-emerald-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Teknik</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Kesi Yeri</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">İz Görünürlüğü</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Transkonjonktival (iç kısımdan)</td>
                      <td className="border border-gray-300 px-4 py-3">Göz kapağının iç kısmı</td>
                      <td className="border border-gray-300 px-4 py-3 text-green-600 font-medium">👁️ İz kalmaz</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Subciliary (kirpik altı kesisi)</td>
                      <td className="border border-gray-300 px-4 py-3">Kirpik hizasının hemen altı</td>
                      <td className="border border-gray-300 px-4 py-3 text-yellow-600 font-medium">🌸 İz minimaldir, birkaç hafta içinde silikleşir</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="text-gray-700 mb-4 leading-relaxed">
                Yani iz, doğru teknikle yapıldığında ya tamamen gizlidir ya da görünmez düzeydedir.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3️⃣ Transkonjonktival (İzsiz) Teknik
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Bu yöntemde kesi, göz kapağının içinden yapılır.
                Cilt kesilmediği için dışarıdan hiçbir iz görülmez.
                Ancak bu teknik, sadece fazla derisi olmayan genç hastalar için uygundur.
              </p>

              <div className="bg-green-50 rounded-xl p-6 mb-6">
                <h4 className="font-semibold text-green-900 mb-4">Avantajları:</h4>
                <ul className="list-disc list-inside space-y-2 text-green-800">
                  <li>Dışarıdan iz kalmaz</li>
                  <li>Şişlik ve morluk daha azdır</li>
                  <li>İyileşme süresi daha kısadır</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4️⃣ Kirpik Altı (Subciliary) Teknik
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Bu yöntem, hem fazla yağ hem de fazla deri alınması gereken hastalarda tercih edilir.
                Kesi kirpik hattının 1–2 mm altından yapılır, bu nedenle iz doğal kirpik gölgesine karışır.
              </p>

              <div className="bg-orange-50 border-l-4 border-orange-400 p-4 mb-6">
                <p className="text-orange-800 font-medium">
                  📍 İz kalır mı?
                </p>
                <p className="text-orange-800 mt-2">
                  Evet, ama bu iz çok ince, açık renkli ve birkaç ay içinde görünmez hale gelir.
                </p>
              </div>

              <div className="bg-emerald-50 border-l-4 border-emerald-400 p-4 mb-6">
                <p className="text-emerald-800 font-medium">
                  "İz değil, ifade değişir.
                  Doğru teknikle yapılan alt kapak ameliyatında güzellik kalır, iz kalmaz."
                </p>
                <p className="text-emerald-800 font-medium mt-2">
                  — Dr. Barış Çin
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                5️⃣ Cilt Tipi İz Oluşumunu Etkiler mi?
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-blue-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Cilt Tipi</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">İyileşme Eğilimi</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Not</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Açık tenli</td>
                      <td className="border border-gray-300 px-4 py-3 text-green-600 font-medium">Çok hızlı</td>
                      <td className="border border-gray-300 px-4 py-3">İz genellikle fark edilmez</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Buğday tenli</td>
                      <td className="border border-gray-300 px-4 py-3 text-yellow-600 font-medium">Normal</td>
                      <td className="border border-gray-300 px-4 py-3">2–3 ayda solar</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Koyu tenli</td>
                      <td className="border border-gray-300 px-4 py-3 text-orange-600 font-medium">Pigment hassasiyeti olabilir</td>
                      <td className="border border-gray-300 px-4 py-3">Güneş koruması şart</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="text-gray-700 mb-4 leading-relaxed">
                Ayrıca iyileşmeyi etkileyen faktörler:
              </p>

              <div className="bg-red-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside space-y-2 text-red-800">
                  <li>Sigara kullanımı 🚭</li>
                  <li>Güneş maruziyeti ☀️</li>
                  <li>Yara bakımına özen gösterilmemesi</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                6️⃣ Dikiş Tekniği de İz Kalitesini Belirler
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Cerrahi iplik seçimi ve dikiş yönü, yaranın iyileşme çizgisini belirler.
              </p>

              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-purple-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Dikiş Tipi</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Özellik</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">İnce, cilt altı emilebilir iplik</td>
                      <td className="border border-gray-300 px-4 py-3 text-green-600 font-medium">İz riskini azaltır</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Cilt dışı kalın dikiş</td>
                      <td className="border border-gray-300 px-4 py-3 text-red-600 font-medium">Mikroskopik iz bırakabilir</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-emerald-50 rounded-xl p-6 mb-6">
                <p className="text-emerald-800 font-medium">
                  Dr. Barış Çin kliniğinde kullanılan teknik:
                </p>
                <p className="text-emerald-800 mt-2">
                  "Mikro sütür" (0.03 mm incelikte, deri altı dikiş) — dikiş izini görünmez kılar.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                7️⃣ İyileşme Süreci
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-teal-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Gün</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Değişiklik</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">1–3</td>
                      <td className="border border-gray-300 px-4 py-3">Hafif morluk ve ödem</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">5–7</td>
                      <td className="border border-gray-300 px-4 py-3">Dikişler alınır</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">10–14</td>
                      <td className="border border-gray-300 px-4 py-3">Şişlik azalır, doğal görünüm başlar</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">30</td>
                      <td className="border border-gray-300 px-4 py-3">İz solmaya başlar</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">90</td>
                      <td className="border border-gray-300 px-4 py-3">İz cilt tonuna tamamen karışır</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                8️⃣ İz Kalmaması İçin Dikkat Edilmesi Gerekenler
              </h2>
              
              <div className="bg-green-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside space-y-2 text-green-800">
                  <li>✅ Güneşten korunmak (SPF 50+ kullanılmalı)</li>
                  <li>✅ Sigara ve alkolü iyileşme döneminde bırakmak</li>
                  <li>✅ Bölgeyi ovuşturmamak</li>
                  <li>✅ Vitamin C ve çinko desteği almak</li>
                  <li>✅ Doktorun önerdiği krem ve silikon jelleri düzenli kullanmak</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                9️⃣ İz Kalırsa Ne Yapılır?
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Nadir de olsa bazı kişilerde hafif pembe veya ince çizgi şeklinde iz kalabilir.
                Bu durumda:
              </p>

              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside space-y-2 text-blue-800">
                  <li>Lazerle iz silme</li>
                  <li>PRP</li>
                  <li>Mikro iğneleme (microneedling)</li>
                </ul>
                <p className="text-blue-800 mt-4">
                  gibi yöntemlerle iz görünümü tamamen yok edilebilir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                🔟 Sık Sorulan Sorular (FAQ)
              </h2>
              
              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">1. Alt göz kapağı ameliyatı kalıcı mı?</h3>
                  <p className="text-gray-600">Evet, yağ torbaları alındıktan sonra tekrarlama nadirdir.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">2. İz mutlaka kalır mı?</h3>
                  <p className="text-gray-600">Hayır, çoğu hastada birkaç haftada tamamen kaybolur.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">3. Morarma olur mu?</h3>
                  <p className="text-gray-600">Evet ama 5–7 gün içinde geçer.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">4. Gözler ne zaman normal görünür?</h3>
                  <p className="text-gray-600">Ortalama 2–3 hafta içinde.</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">5. Hangi teknik daha iyi?</h3>
                  <p className="text-gray-600">Hastanın durumuna göre doktor karar verir. Genç hastalarda transkonjonktival, yaşlı hastalarda subciliary tercih edilir.</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">6. Ameliyat sonrası ne kadar süre işe dönemem?</h3>
                  <p className="text-gray-600">Genellikle 1 hafta sonra işe dönülebilir, tam iyileşme 2-3 hafta sürer.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1️⃣1️⃣ Uzman Görüşü
              </h2>
              
              <div className="bg-blue-50 border-l-4 border-blue-400 p-6 mb-6">
                <p className="text-blue-800 italic">
                  "Alt göz kapağı ameliyatında amaç, gençlik değil, doğallıktır.
                  Göz çevresi ifadenin merkezidir; izi değil, ışığı fark edilir."
                </p>
                <p className="text-blue-800 font-medium mt-4">
                  — Dr. Barış Çin, Estetik Cerrahi Uzmanı
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1️⃣2️⃣ Etik ve Klinik Değerlendirme
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Alt göz kapağı cerrahisi, küçük bir kesiyle büyük bir değişim sağlar.
                Ancak her ameliyatta olduğu gibi, iz riskini azaltan en önemli faktör cerrahın deneyimidir.
              </p>

              <div className="bg-purple-50 border-l-4 border-purple-400 p-4 mb-6">
                <p className="text-purple-800 font-medium">
                  💬 "Estetik cerrahide sihirli krem yoktur; sihirli eller vardır."
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1️⃣3️⃣ Sonuç ve Klinik Öneri
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Alt göz kapağı ameliyatı doğru teknikle yapıldığında iz kalma olasılığı yok denecek kadar azdır.
                Kesi hattı gizli, sonuç ise doğal olur.
              </p>

              <div className="bg-emerald-50 border-l-4 border-emerald-400 p-6 mb-6">
                <p className="text-emerald-800 font-medium">
                  🩺 Dr. Barış Çin öneriyor:
                </p>
                <p className="text-emerald-800 mt-2">
                  "Korkulan iz, doğru cerrahta sadece bir çizgi olur — o da gülümseyince kaybolur."
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaynakça
              </h2>
              
              <div className="bg-gray-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>ISAPS (2024) – "Lower Blepharoplasty Scar Outcome Study"</li>
                  <li>Aesthetic Surgery Journal (2023) – "Subciliary vs. Transconjunctival Comparison"</li>
                  <li>TETD (2024) – "Blefaroplasti Sonrası Yara Bakımı Rehberi"</li>
                  <li>Harvard Medical Aesthetics (2024) – "Periorbital Scar Healing Mechanisms"</li>
                </ul>
              </div>

              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-sm">#altgözkapağıameliyatı</span>
                <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-sm">#blefaroplasti</span>
                <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-sm">#DrBarışÇin</span>
                <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-sm">#estetikcerrahi</span>
                <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-sm">#izkalırmı</span>
              </div>

              {/* Social Share Buttons */}
              <div className="border-t pt-8 mt-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Bu yazıyı paylaş:</h3>
                <div className="flex space-x-4">
                  <button
                    onClick={() => handleShare('facebook')}
                    className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <FaFacebookF className="w-4 h-4" />
                    <span>Facebook</span>
                  </button>
                  <button
                    onClick={() => handleShare('twitter')}
                    className="flex items-center space-x-2 bg-blue-400 text-white px-4 py-2 rounded-lg hover:bg-blue-500 transition-colors"
                  >
                    <FaTwitter className="w-4 h-4" />
                    <span>Twitter</span>
                  </button>
                  <button
                    onClick={() => handleShare('linkedin')}
                    className="flex items-center space-x-2 bg-blue-700 text-white px-4 py-2 rounded-lg hover:bg-blue-800 transition-colors"
                  >
                    <FaLinkedinIn className="w-4 h-4" />
                    <span>LinkedIn</span>
                  </button>
                  <button
                    onClick={() => handleShare('whatsapp')}
                    className="flex items-center space-x-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors"
                  >
                    <FaWhatsapp className="w-4 h-4" />
                    <span>WhatsApp</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>

        {/* CTA Section */}
        <div className="mt-12 bg-gradient-to-r from-emerald-600 to-teal-700 rounded-3xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-4">Alt Göz Kapağı Ameliyatı İçin Konsültasyon Alın</h3>
          <p className="mb-6">Uzman doktorumuzdan alt göz kapağı ameliyatı hakkında detaylı bilgi ve değerlendirme alın.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`https://wa.me/905438999586?text=${encodeURIComponent('Alt göz kapağı ameliyatı hakkında bilgi almak istiyorum.')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
            >
              <FaWhatsapp className="w-5 h-5 mr-2" />
              WhatsApp ile İletişim
            </a>
            <a
              href="/tr/iletisim"
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-emerald-600 rounded-full hover:bg-gray-100 transition-colors"
            >
              Randevu Al
            </a>
          </div>
        </div>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <a
              href="/tr/blog/goz-alti-dolgusunun-tehlikeleri-bilimsel-gercekler-ve-guvenli-uygulama-kriterleri"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Göz Altı Dolgusunun Tehlikeleri</h4>
              <p className="text-gray-600 text-sm">Bilimsel gerçekler ve güvenli uygulama kriterleri.</p>
            </a>
            <a
              href="/tr/blog/goz-alti-dolgusunun-onerilmedigi-durumlar-kimler-icin-uygun-degil"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Göz Altı Dolgusunun Önerilmediği Durumlar</h4>
              <p className="text-gray-600 text-sm">Kimler için uygun değil?</p>
            </a>
            <a
              href="/tr/blog/ameliyatsiz-yuz-germe-yontemleri"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Ameliyatsız Yüz Germe Yöntemleri</h4>
              <p className="text-gray-600 text-sm">Cerrahi olmayan yüz gençleştirme seçenekleri.</p>
            </a>
            <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
              <p className="text-gray-600 mb-4">
                Daha fazla bilgilendirici içerik için bizi takip edin.
              </p>
              <a
                href="/tr/blog"
                className="inline-flex items-center px-4 py-2 bg-emerald-600 text-white rounded-full hover:bg-emerald-700 transition-colors text-sm"
              >
                Tüm Blog Yazılarını Görüntüle
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
